import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WarpUser, WarpUserCriteria } from "api/models/warpMaintenance";
import { warpMaintenanceService } from "api/services";
import { RootState } from "app";

type WarpMaintenanceState = {
    users: WarpUser[];
    availableWarps: string[];
    loading: boolean;
};

const initialState: WarpMaintenanceState = {
    users: [],
    availableWarps: [],
    loading: false,
};

export const getWarpUsers = createAsyncThunk(
    "warpMaintenance/users/get",
    async (criteria?: WarpUserCriteria) => await warpMaintenanceService.getUsers(criteria)
);

export const getAvailableWarps = createAsyncThunk(
    "warpMaintenance/warps/get",
    async () => await warpMaintenanceService.getWarps()
);

export const saveWarp = createAsyncThunk(
    "warpMaintenance/warp/save",
    async ({ userId, warp }: { userId: string; warp: string }) => {
        await warpMaintenanceService.saveWarp(userId, warp);
        return { userId, warp };
    }
);

const warpMaintenanceSlice = createSlice({
    name: "warpMaintenance",
    initialState,
    reducers: {
        clearWarpUsers: (state) => {
            state.users = [];
        },
    },
    extraReducers: (builder) => {
        builder
          .addCase(getWarpUsers.pending, (state) => {
              state.loading = true;
          })
          .addCase(getWarpUsers.fulfilled, (state, action: PayloadAction<WarpUser[] | null>) => {
              state.loading = false;
              state.users = action.payload ?? [];
          })
          .addCase(getWarpUsers.rejected, (state) => {
              state.loading = false;
          })
          .addCase(getAvailableWarps.fulfilled, (state, action: PayloadAction<string[] | null>) => {
              state.availableWarps = action.payload ?? [];
          })
          .addCase(saveWarp.fulfilled, (state, action) => {
              const user = state.users.find(u => u.id === action.payload.userId);
              if (user) user.warp = action.payload.warp;
          });
    },
});

export const { clearWarpUsers } = warpMaintenanceSlice.actions;
export const selectWarpUsers = (state: RootState) => state.warpMaintenance.users;
export const selectAvailableWarps = (state: RootState) => state.warpMaintenance.availableWarps;
export const selectWarpMaintenanceLoading = (state: RootState) => state.warpMaintenance.loading;

export default warpMaintenanceSlice.reducer;
